import RoutePath from './RoutePath'

/**
 * Route Builder Helper
 *
 * Type-safe utility for building routes with parameters.
 * Automatically infers required and optional parameters from route paths.
 *
 * @example
 * ```ts
 * // Route: '/checkin/:enrollmentId/:activityId?'
 * buildRoute(RoutePath.CHECKIN, {
 *   enrollmentId: '123',
 *   activityId: '456' // optional
 * })
 * // => '/checkin/123/456'
 *
 * buildRoute(RoutePath.CHECKIN, {
 *   enrollmentId: '123'
 * })
 * // => '/checkin/123'
 * ```
 */

// Extract parameter names from a route path string
// Examples:
//   '/checkin/:enrollmentId/:activityId?' => 'enrollmentId' | 'activityId?'
//   '/pay/:enrollmentId' => 'enrollmentId'
type ExtractRouteParams<T extends string> = T extends `${string}:${infer Param}/${infer Rest}`
  ? Param | ExtractRouteParams<`/${Rest}`>
  : T extends `${string}:${infer Param}`
    ? Param
    : never

// Split parameter names into required and optional
// Examples:
//   'enrollmentId' | 'activityId?' => { required: 'enrollmentId', optional: 'activityId' }
type SplitRequiredOptional<T extends string> = {
  required: T extends `${string}?` ? never : T
  optional: T extends `${infer Name}?` ? Name : never
}

// Build the params object type from route params
// Required params are required properties, optional params are optional properties
type RouteParamsType<T extends string> =
  ExtractRouteParams<T> extends never
    ? Record<string, never> // No params
    : {
        [K in SplitRequiredOptional<ExtractRouteParams<T>>['required']]: string
      } & {
        [K in SplitRequiredOptional<ExtractRouteParams<T>>['optional']]?: string
      }

/**
 * Build a route URL by replacing parameters in the route path.
 *
 * @param routePath - The route from RoutePath enum
 * @param params - Object containing parameter values. TypeScript will enforce required parameters.
 * @param queryParams - Optional query parameters to append to the URL
 * @returns The constructed URL with parameters replaced
 *
 * @example
 * ```ts
 * // No params
 * buildRoute(RoutePath.LOGOUT)
 * // => '/logout'
 *
 * // No params with query params (second arg is query params)
 * buildRoute(RoutePath.DASHBOARD, { tab: 'overview' })
 * // => '/dashboard?tab=overview'
 *
 * // Required param
 * buildRoute(RoutePath.PAY, { enrollmentId: '123' })
 * // => '/pay/123'
 *
 * // Optional param included
 * buildRoute(RoutePath.CHECKIN, {
 *   enrollmentId: '123',
 *   activityId: '456'
 * })
 * // => '/checkin/123/456'
 *
 * // Optional param omitted
 * buildRoute(RoutePath.CHECKIN, {
 *   enrollmentId: '123'
 * })
 * // => '/checkin/123'
 *
 * // With query params (third arg)
 * buildRoute(RoutePath.CHECKIN,
 *   { enrollmentId: '123' },
 *   { source: 'qr', tab: 'details' }
 * )
 * // => '/checkin/123?source=qr&tab=details'
 * ```
 */

// Overload: Route with no parameters
export function buildRoute<T extends RoutePath>(
  routePath: T extends RoutePath ? (RouteParamsType<T> extends Record<string, never> ? T : never) : never,
  queryParams?: Record<string, string | number | boolean | undefined>
): string

// Overload: Route with parameters
export function buildRoute<T extends RoutePath>(
  routePath: T,
  params: RouteParamsType<T>,
  queryParams?: Record<string, string | number | boolean | undefined>
): string

// Implementation
export function buildRoute<T extends RoutePath>(
  routePath: T,
  paramsOrQueryParams?: RouteParamsType<T> | Record<string, string | number | boolean | undefined>,
  queryParams?: Record<string, string | number | boolean | undefined>
): string {
  // Determine if the route has any parameters at runtime
  const routeHasParams = (routePath as string).includes(':')

  // If route has params OR there's a third argument (old syntax), treat as params + query
  // If route has no params and no third argument, second arg is query params
  const isOldSyntax = queryParams !== undefined
  const params = (routeHasParams || isOldSyntax ? paramsOrQueryParams : {}) as RouteParamsType<T>
  const finalQueryParams =
    routeHasParams || isOldSyntax
      ? queryParams
      : (paramsOrQueryParams as Record<string, string | number | boolean | undefined> | undefined)

  let result = routePath as string

  // Replace all parameters in the route
  // Match :paramName or :paramName? patterns
  const paramRegex = /:([a-zA-Z0-9_]+)\??/g

  result = result.replace(paramRegex, (match, paramName) => {
    const paramValue = (params as Record<string, string | undefined>)[paramName]

    // If optional parameter is not provided, remove it including the preceding slash
    if (paramValue === undefined || paramValue === '') {
      // Check if this is an optional parameter (ends with ?)
      if (match.endsWith('?')) {
        return ''
      }
      // Required parameter is missing - this shouldn't happen with proper types
      throw new Error(`Required parameter '${paramName}' is missing`)
    }

    return paramValue
  })

  // Clean up any double slashes or trailing slashes that might have been created
  result = result.replace(/\/+/g, '/').replace(/\/$/, '')

  // If the result is empty, return root
  if (result === '') {
    result = '/'
  }

  // Add query parameters if provided
  if (finalQueryParams) {
    const queryString = Object.entries(finalQueryParams)
      .filter(([, value]) => value !== undefined && value !== '')
      .map(([key, value]) => `${encodeURIComponent(key)}=${encodeURIComponent(String(value))}`)
      .join('&')

    if (queryString) {
      result += `?${queryString}`
    }
  }

  return result
}
